import { useQueryClient } from 'react-query'
import {
  MembershipPackage,
  MembershipPackageDiscontinueParams,
  SessionsApi,
} from '@citruscamps/citrus-client'
import { useToast } from '../../../../../hooks/useToast'
import { generateBaseKey } from '../../../../../utils/key-generator'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'
import translations from '../../../../../translations/en.json'
import { useRouter } from '../../../../../hooks/useRouter'
import { generateApiConfig } from '../../../../../utils/client-config'

interface IProps {
  programId?: string
}

interface IUpdateMemberPackages {
  handleUpdate: (value: MembershipPackage) => Promise<void>
  handleDelete: (value: MembershipPackage) => Promise<void>
  handleDiscontinue: (value: MembershipPackageDiscontinueParams) => Promise<void>
  handleRecontinue: (value: MembershipPackage) => Promise<void>
}

export const useUpdateMembershipPackages = ({ programId }: IProps): IUpdateMemberPackages => {
  const { requestHandler } = useRequestHandler()
  const queryClient = useQueryClient()
  const { setToast } = useToast()
  const router = useRouter()

  const handleUpdate = async (value: MembershipPackage): Promise<void> => {
    if (programId && value?.id) {
      const client = new SessionsApi(generateApiConfig())
      await requestHandler<MembershipPackage>(
        () =>
          client.updateMembershipPackage({
            program_id: programId,
            membership_package_id: value.id,
            MembershipPackage: value,
          }),
        {
          toast: {
            setToast,
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'membership_package',
          program_id: programId,
        }),
      )
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleDelete = async (value: MembershipPackage): Promise<void> => {
    const memberPackClient = new SessionsApi(generateApiConfig())
    if (programId && value?.id) {
      try {
        await requestHandler(() =>
          memberPackClient.deleteMembershipPackage({
            program_id: programId,
            membership_package_id: value.id,
          }),
        )
        if (router.pathname.includes(value.id)) {
          router.history.goBack()
        }
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'membership_package',
            program_id: programId,
          }),
        )
        setToast({
          header: (translations as any)['success.default_toast_header'],
          message: (translations as any)['success.default_toast_message'],
          type: 'success',
        })
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleDiscontinue = async (value: MembershipPackageDiscontinueParams): Promise<void> => {
    const memberPackClient = new SessionsApi(generateApiConfig())
    if (programId && value?.id) {
      try {
        await requestHandler(() =>
          memberPackClient.discontinueMembershipPackage({
            program_id: programId,
            membership_package_id: value.id,
            MembershipPackageDiscontinueParams: value,
          }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'membership_package',
            program_id: programId,
          }),
        )
        setToast({
          header: (translations as any)['success.default_toast_header'],
          message: (translations as any)['success.update_package_message'],
          type: 'success',
        })
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleRecontinue = async (value: MembershipPackage): Promise<void> => {
    const memberPackClient = new SessionsApi(generateApiConfig())
    if (programId && value?.id) {
      try {
        await requestHandler(() =>
          memberPackClient.recontinueMembershipPackage({
            program_id: programId,
            membership_package_id: value.id,
          }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'membership_package',
            program_id: programId,
          }),
        )
        setToast({
          header: (translations as any)['success.default_toast_header'],
          message: (translations as any)['success.update_package_message'],
          type: 'success',
        })
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  return {
    handleUpdate,
    handleDelete,
    handleDiscontinue,
    handleRecontinue,
  }
}
